
// TODO take a closer look at how categories of assets are assigned 
// Simply taking them from their parent card might not always be correct (probably quite often wrong => not for fetch but for intermediate/commit)

import APPS from '@generio-ai/library-legacy/constants/apps'
import ASSETS from '@generio-ai/library-legacy/constants/assets'
import CATEGORY from '@generio-ai/library-legacy/constants/category'
import CARD from "@/constants/card.js"
import { Card } from "@/classes/card.js"
import { Prompt } from "@/classes/prompt.js"
import { generateUniqueIdentifier } from '@/utils/identifier.js'
import actionsOpen from './actions/open'
import actionsUpgrade from './actions/upgrade.js'
import actionsTransaction from './actions/transaction.js'
import actionsCommit from './actions/commit.js'
import actionsFetch from './actions/fetch.js'
import actionsIntermediate from './actions/intermediate.js'
import actionsUtils from './actions/utils.js'


function setFlow(state, app, flowId, cards)
{
    // set flow state
    state.flows[app] = {
        id: flowId,
        cards: cards,
        activeIndex: cards.length - 1
    }
}

function resetFlow(state, app) 
{
    // generate new unique identifier
    const flowId = generateUniqueIdentifier()

    // reset root card
    const rootCard = state.flows[app].cards[0]
    rootCard.reset()
    rootCard.flow = flowId
    rootCard.assets = null

    // set flow to reset state
    setFlow(state, app, flowId, [ rootCard ])
}

const flows = {
    namespaced: true,
    state: {
        flows: {}
    },
    mutations: {

        //// APP

        // initial registration for each flow-based app
        REGISTER_APP(state, { 
            app, 
            rootCard 
        }) 
        {
            if (app in state.flows) return
            
            // set initial flow state
            setFlow(state, app, generateUniqueIdentifier(), [ rootCard ])
        },


        //// FLOW

        // create a new flow
        NEW_FLOW(state, { 
            app
        }) 
        {
            // check if app is registered
            if (!(app in state.flows)) 
                throw new Error(`App '${app}' is not registered in flows store.`)

            // reset flow
            resetFlow(state, app)
        },

        // open an existing flow
        OPEN_FLOW(state, {
            app,
            flowId,
            cards,
            activeIndex = null
        })
        {
            // check if app is registered
            if (!(app in state.flows)) 
                throw new Error(`App '${app}' is not registered in flows store.`)

            // set flow state
            state.flows[app] = {
                id: flowId,
                cards: cards,
                activeIndex: activeIndex !== null ? activeIndex : cards.length - 1
            }
        },

        // set flow id
        SET_FLOW_ID(state, {
            app,
            flowId
        }) {
            // check if app is registered
            if (!(app in state.flows)) 
                throw new Error(`App '${app}' is not registered in flows store.`)

            // set new flow id
            state.flows[app].id = flowId
        },


        //// CARD

        // add a new card
        ADD_CARD(state, {
            app,
            card
        })
        {
            // check if app is registered
            if (!(app in state.flows)) 
                throw new Error(`App '${app}' is not registered in flows store.`)

            // add card
            state.flows[app].cards.push(card)
            state.flows[app].activeIndex = state.flows[app].cards.length - 1
        },

        // remove an existing card
        REMOVE_CARD(state, {
            app,
            index
        })
        {
            // check if app is registered
            if (!(app in state.flows)) 
                throw new Error(`App '${app}' is not registered in flows store.`)

            // if index is not a valid value, nothing to remove
            if (index == null || index == undefined) return

            // if index is out of bounds, nothing to remove
            if (index < 0 || index >= state.flows[app].cards.length) return

            // if index is first card, reset entire flow
            if (index === 0) {
                resetFlow(state, app)
                return
            }

            // remove all items after the given index
            state.flows[app].cards.splice(index)

            // adjust activeIndex if necessary
            if (state.flows[app].activeIndex > index) {
                state.flows[app].activeIndex = index
            }
        },

        // reset the card state
        RESET_CARD(state, {
            app,
            index
        })
        {
            // check if app is registered
            if (!(app in state.flows)) 
                throw new Error(`App '${app}' is not registered in flows store.`)

            if (state.flows[app].cards.length <= index) return
            const card = state.flows[app].cards[index]
            card.option = null
            card.action = null
        },

        // set active card by index
        SET_ACTIVE_CARD_BY_INDEX(state, {
            app,
            index
        })
        {
            // check if app is registered
            if (!(app in state.flows)) 
                throw new Error(`App '${app}' is not registered in flows store.`)

            // check if index is valid
            if (index < 0 || index >= state.flows[app].cards.length) return
            state.flows[app].activeIndex = index
        }
    },
    getters: {
        isRegistered: state => app => app in state.flows,
        isActiveByIndex: state => (app, index) => {
            if (!(app in state.flows)) return false
            return state.flows[app].activeIndex === index
        },
        isLastByIndex: state => (app, index) => {
            if (!(app in state.flows)) return false
            return index === state.flows[app].cards.length - 1
        },
        
        isClonableByCard: () => (card) => {
            return typeof card?.transaction?.taskFetch?.clone === 'function'
        },

        isFullscreen: (state, getters, rootState, rootGetters) => {
            const additional = rootGetters['user/getAdditional']
            return additional?.flowsFullscreen || false 
        },

        isExpertMode: (state, getters, rootState, rootGetters) => {
            const additional = rootGetters['user/getAdditional']
            if (!rootGetters['auth/isAdministrator']) return false
            return additional?.flowsExpertMode === undefined ? false : additional.flowsExpertMode
        },
        isDebugCard: (state, getters, rootState, rootGetters) => {
            const additional = rootGetters['user/getAdditional']
            if (!rootGetters['auth/isAdministrator']) return false
            return additional?.flowsDebugCard === undefined ? false : additional.flowsDebugCard
        },
        isDebugLayer: (state, getters, rootState, rootGetters) => {
            const additional = rootGetters['user/getAdditional']
            if (!rootGetters['auth/isAdministrator']) return false
            return additional?.flowsDebugLayer === undefined ? false : additional.flowsDebugLayer
        },
        isGenerationRandom: (state, getters, rootState, rootGetters) => {
            const additional = rootGetters['user/getAdditional']
            if (!rootGetters['auth/isAdministrator']) return true
            return additional?.flowsGenerationRandom === undefined ? true : additional.flowsGenerationRandom
        },

        isInputAsset: () => (asset) => {
            if (asset?.app === APPS.AGENT) return asset.type === ASSETS.TYPES.IMAGE
            if ([APPS.CREATE, APPS.SKETCH2, APPS.SKETCH].includes(asset?.app)) {
                return asset.type === ASSETS.TYPES.IMAGE
            }
            if ([APPS.MODIFY].includes(asset?.app)) {
                return asset.type === ASSETS.TYPES.MODEL && asset.additional?.card?.index === 0
            }
            return false
        },
        isOutputAsset: () => (asset) => {
            if (asset?.app === APPS.AGENT) return asset.type === ASSETS.TYPES.MODEL
            if ([APPS.CREATE, APPS.SKETCH2, APPS.SKETCH].includes(asset?.app)) {
                return asset.type === ASSETS.TYPES.MODEL
            }
            if ([APPS.MODIFY].includes(asset?.app)) {
                return asset.type === ASSETS.TYPES.MODEL && asset.additional?.card?.index !== 0
            }
            return false
        },

        isDualCard: () => (card) => {
            return card?.prompt?.dual ?? false
        },

        hasChanges: state => app => {
            if (!(app in state.flows)) return false
            if (state.flows[app].cards.length > 1) return true
            const rootCard = state.flows[app].cards?.[0]
            return rootCard.option != null || rootCard.action != null
        },
        hasMultiple: state => app => {
            if (!(app in state.flows)) return false
            return state.flows[app].cards.length > 1
        },

        queryAsset: () => (asset, query) => {
            if (!asset) return false
            if (!query || query.length === 0) return true

            return asset.additional?.positive?.toLowerCase().includes(query) ||
                asset.additional?.prompt?.positive?.toLowerCase().includes(query) || 
                asset.additional?.prompt?.user?.toLowerCase().includes(query) ||
                asset.additional?.prompt?.system?.toLowerCase().includes(query)
        },

        getAllAssets: (state, getters, rootState, rootGetters) => {
            return rootGetters['assets/getAssets'].filter(asset => {
                const supportedType = [ASSETS.TYPES.IMAGE, ASSETS.TYPES.MODEL].includes(asset?.type) ||
                    (asset?.app === APPS.AGENT && asset?.type === ASSETS.TYPES.PRIMITIVE)
                return supportedType &&
                    [APPS.CREATE, APPS.MODIFY, APPS.SKETCH, APPS.SKETCH2, APPS.AGENT].includes(asset?.app) &&
                    asset?.additional?.ignore !== true
            })
        },

        getFlowId: state => app => {
            if (!(app in state.flows)) return null
            return state.flows[app].id
        },

        getCards: state => app => {
            if (!(app in state.flows)) return []
            return state.flows[app].cards
        },
        getCount: state => app => {
            if (!(app in state.flows)) return 0
            return state.flows[app].cards.length
        },
        getActiveCard: state => app => {
            if (!(app in state.flows)) return null
            const index = state.flows[app].activeIndex
            return state.flows[app].cards[index] || null
        },
        getActiveIndex: state => app => {
            if (!(app in state.flows)) return 0
            return state.flows[app].activeIndex
        },
        getFirstCard: state => app => {
            if (!(app in state.flows)) return null
            return state.flows[app].cards[0] || null
        },
        getLastCard: state => app => {
            if (!(app in state.flows)) return null
            const cards = state.flows[app].cards
            return cards[cards.length - 1] || null
        },
        getParentCard: state => (app, card) => {
            if (!(app in state.flows)) return null
            if (card.index <= 0) return null
            return state.flows[app].cards[card.index - 1] || null
        },

        getPrimaryCardForIndex: (state) => (app, index, inclusive = true) => {
            if (index === null || index === undefined) return null
            const start = inclusive ? index : index - 1
            for (let i = start; i >= 0; i--) {
                const card = state.flows[app].cards[i]
                if (!card.skipped && !card.primitive) {
                    return card
                }
            }
            return null
        },
        getPrimaryCardForCard: (state, getters) => (app, card, inclusive = true) => {
            if (card == null) return null
            return getters.getPrimaryCardForIndex(app, card.index, inclusive)
        },

        getCardByCategories: (state, getters) => (app, card, categories, inclusive = true) => {
            if (card.index === null || card.index === undefined) return null
            const start = inclusive ? card.index : card.index - 1
            for (let i = start; i >= 0; i--) 
            {
                const card = state.flows[app].cards[i]
                if (!card.skipped && !card.primitive && categories.includes(card.category)) {
                    return card
                }
            }
            return null
        },

        getAssetsForFlow: (state, getters, rootState, rootGetters) => (app, flowId) => {
            return rootGetters['assets/getAssets'].filter(asset => 
                asset.app === app && asset.additional?.flow === flowId
            )
        }
    },
    actions: {

        init({ dispatch }) 
        {
            // create custom prompt for MODIFY app
            const modifyPrompt = new Prompt()
            modifyPrompt.dual = true

            // all apps that need registering
            const apps = {
                [APPS.AGENT]: new Card(null, 0, CARD.AGENT_INPUT, CATEGORY.PRIMITIVE, new Prompt()),
                [APPS.CREATE]: new Card(
                        null,
                        0,
                        CARD.SELECT_FLOW,
                        CATEGORY.PRIMITIVE,
                        new Prompt(),
                        null
                    ),
                [APPS.MODIFY]: new Card(
                    null,
                    0,
                    CARD.IMPORT,
                    CATEGORY.MODEL,
                    modifyPrompt,
                    null
                )
            }

            // iterate through all apps
            for (const [app, rootCard] of Object.entries(apps)) {
                // register app
                dispatch('registerApp', {
                    app,
                    rootCard
                })
            }
        },


        //// APP

        registerApp({ commit }, {
            app,
            rootCard
        }) {
            commit('REGISTER_APP', {
                app,
                rootCard
            })
        },


        //// FLOW

        newFlow({ commit }, {
            app
        }) {
            commit('NEW_FLOW', {
                app
            })
        },


        //// CARD

        removeCard({ commit }, { 
            app,
            index
        }) {
            commit('REMOVE_CARD', {
                app,
                index
            })
            commit('RESET_CARD', {
                app,
                index: index - 1
            })
        },

        removeActiveCard({ commit, getters }, {
            app
        }) {
            const activeIndex = getters.getActiveIndex(app)
            commit('REMOVE_CARD', {
                app,
                index: activeIndex
            })
            if (activeIndex > 0) {
                commit('RESET_CARD', {
                    app,
                    index: activeIndex - 1
                })
            }
        },

        setActiveIndex({ commit }, {
            app,
            index
        }) {
            commit('SET_ACTIVE_CARD_BY_INDEX', {
                app,
                index
            })
        },

        setActiveIndexToLast({ commit, getters }, {
            app
        }) {
            commit('SET_ACTIVE_CARD_BY_INDEX', {
                app,
                index: getters.getCount(app) - 1
            })
        },

        setActiveIndexToNext({ commit, getters }, {
            app
        }) {
            const nextIndex = getters.getActiveIndex(app) + 1
            if (nextIndex < getters.getCount(app)) {
                commit('SET_ACTIVE_CARD_BY_INDEX', {
                    app,
                    index: nextIndex
                })
            }
        },


        //// SETTINGS

        setFullscreen({ dispatch, rootGetters }, { 
            fullscreen 
        }) {
            let additional = rootGetters['user/getAdditional'] || {}
            additional.flowsFullscreen = fullscreen
            dispatch('user/setAdditional', { additional }, { root: true })
        },

        toggleFullscreen({ dispatch, rootGetters }) 
        {
            let additional = rootGetters['user/getAdditional'] || {}
            if (additional.flowsFullscreen) additional.flowsFullscreen = false
            else additional.flowsFullscreen = true
            dispatch('user/setAdditional', { additional }, { root: true })
        },

        setExpertMode({ dispatch, rootGetters }, {
            expert
        }) {
            let additional = rootGetters['user/getAdditional'] || {}
            additional.flowsExpertMode = expert
            dispatch('user/setAdditional', { additional }, { root: true })
        },
        setDebugCard({ dispatch, rootGetters }, {
            debug
        }) {
            let additional = rootGetters['user/getAdditional'] || {}
            additional.flowsDebugCard = debug
            dispatch('user/setAdditional', { additional }, { root: true })
        },
        setDebugLayer({ dispatch, rootGetters }, {
            debug
        }) {
            let additional = rootGetters['user/getAdditional'] || {}
            additional.flowsDebugLayer = debug
            dispatch('user/setAdditional', { additional }, { root: true })
        },
        setGenerationRandom({ dispatch, rootGetters }, {
            random
        }) {
            let additional = rootGetters['user/getAdditional'] || {}
            additional.flowsGenerationRandom = random
            dispatch('user/setAdditional', { additional }, { root: true })
        },


        //// IMPORTED ACTIONS

        ...actionsOpen,
        ...actionsUpgrade,
        ...actionsTransaction,
        ...actionsCommit,
        ...actionsFetch,
        ...actionsIntermediate,
        ...actionsUtils
    }
}

export default flows
